import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/common/Layout';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import KanbanBoard from '../components/kanban/KanbanBoard';
import StepSidebar from '../components/template/StepSidebar';
import QuestionDetailSidebar from '../components/sidebar/QuestionDetailSidebar';
import type { Template, Question, Step, UserResponse, Organization, User } from '../types';

const RespondTemplate: React.FC = () => {
  const navigate = useNavigate();
  const { orgId, userId } = useParams<{ orgId: string; userId: string }>();
  const { user } = useAuth();
  
  const [template, setTemplate] = useState<Template | null>(null);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [userResponses, setUserResponses] = useState<UserResponse[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [targetUser, setTargetUser] = useState<User | null>(null);
  
  // Step navigation state
  const [activeStepId, setActiveStepId] = useState<number>(1);

  // New header state
  const [autoSavedVisible, setAutoSavedVisible] = useState(false);
  const [currentMode, setCurrentMode] = useState<'prep' | 'present' | 'plan'>(() => {
    const savedMode = localStorage.getItem('respondTemplateMode');
    return (savedMode === 'present' || savedMode === 'prep' || savedMode === 'plan') ? savedMode : 'prep';
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Present Mode state
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [selectedQuestionOrder, setSelectedQuestionOrder] = useState<number | null>(null);

  // API state
  const [steps, setSteps] = useState<Step[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Helper function to refresh questions after CRUD operations
  const refreshQuestions = async () => {
    if (!template) return;
    
    try {
      const updatedTemplate = await apiService.getTemplate(template.id);
      setQuestions(updatedTemplate.questions);
      setTemplate(updatedTemplate);
    } catch (err) {
      console.error('Failed to refresh questions:', err);
      toast.error('Failed to refresh questions');
    }
  };

  // Handle mode change with localStorage persistence
  const handleModeChange = (mode: 'prep' | 'present' | 'plan') => {
    setIsTransitioning(true);
    
    // Small delay to allow fade out animation
    setTimeout(() => {
      setCurrentMode(mode);
      localStorage.setItem('respondTemplateMode', mode);
      
      // Small delay to allow the DOM to update before fade in
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  };

  // Handle reordering a question (disabled for client users)
  const handleReorderQuestion = async (questionId: number, newStepNumber: number, newOrder: number): Promise<void> => {
    // Disabled for client users - no reordering allowed
    toast.error('Question reordering is not allowed');
    throw new Error('Question reordering is not allowed');
  };

  // Handle questions update from KanbanBoard
  const handleQuestionsUpdate = (updatedQuestions: Question[]) => {
    setQuestions(updatedQuestions);
  };

  // Handle step selection from sidebar
  const handleStepSelect = (stepNumber: number) => {
    setActiveStepId(stepNumber);
  };

  // Handle question click in Present Mode
  const handleQuestionClick = (questionId: number, questionOrder: number) => {
    setSelectedQuestionId(questionId);
    setSelectedQuestionOrder(questionOrder);
  };

  // Disabled handlers for client users (no editing allowed)
  const handleEditQuestionFromKanban = (question: Question) => {
    // Disabled for client users
    toast.error('Question editing is not allowed');
  };

  const handleDeleteQuestionFromKanban = async (question: Question) => {
    // Disabled for client users
    toast.error('Question deletion is not allowed');
  };

  const handleAddQuestionFromKanban = (stepNumber: number) => {
    // Disabled for client users
    toast.error('Question addition is not allowed');
  };

  const handleInsertQuestion = (stepNumber: number, insertAtOrder: number) => {
    // Disabled for client users
    toast.error('Question insertion is not allowed');
  };

  // Fallback steps for when API is not available
  const fallbackSteps: Step[] = [
    { id: 1, step_number: 1, step_name: 'Awareness', step_description: null },
    { id: 2, step_number: 2, step_name: 'Understanding', step_description: null },
    { id: 3, step_number: 3, step_name: 'Expectations', step_description: null },
    { id: 4, step_number: 4, step_name: 'Systems', step_description: null },
    { id: 5, step_number: 5, step_name: 'Follow-Up', step_description: null }
  ];

  // Fetch template data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Validate route parameters
        const orgIdNum = orgId ? parseInt(orgId, 10) : null;
        const userIdNum = userId ? parseInt(userId, 10) : null;
        
        if (!orgIdNum || isNaN(orgIdNum) || !userIdNum || isNaN(userIdNum)) {
          throw new Error('Invalid organization or user ID');
        }
        
        // Fetch organization data to get the template assigned to this org
        const organizations = await apiService.getAllOrganizations();
        const organization = organizations.find(org => org.id === orgIdNum);
        console.log(organization);
        if (!organization) {
          throw new Error('Organization not found');
        }

        // Find the target user
        const targetUser = organization.users.find(u => u.id === userIdNum);
        if (!targetUser) {
          throw new Error('User not found in organization');
        }

        console.log(targetUser);
        
        // Store organization and target user data
        setOrganization(organization);
        setTargetUser(targetUser);
        
        // Get the management template assigned to this organization
        // The /responses route is specifically for management template responses
        const orgTemplate = organization.templates.find(t => t.template_type === 'management');
        if (!orgTemplate) {
          throw new Error('No management template assigned to this organization');
        }
        
        setTemplateId(orgTemplate.id);
        
        // Fetch template with questions, steps, and user responses in parallel
        const [fetchedTemplate, fetchedSteps] = await Promise.all([
          apiService.getTemplate(orgTemplate.id),
          apiService.getSteps()
        ]);

        let responsesData: UserResponse[] = [];
        try {
          responsesData = await apiService.getUserResponses(userIdNum);
        } catch (responseError) {
          console.warn('Failed to fetch user responses, using empty array:', responseError);
          responsesData = [];
        }

        setTemplate(fetchedTemplate);
        setTemplateName(fetchedTemplate.name);
        setTemplateDescription(fetchedTemplate.description);
        setQuestions(fetchedTemplate.questions);
        setSteps(fetchedSteps);
        
        // Filter responses for this specific template
        const templateResponses = responsesData.filter(
          response => response.template_id === orgTemplate.id
        );
        
        setUserResponses(templateResponses);
        
        setError(null);
      } catch (err) {
        console.warn('Failed to fetch data from API, using fallback:', err);
        setSteps(fallbackSteps);
        setQuestions([]); // Empty questions for fallback
        setTemplateName('Manager Skills Development');
        setTemplateDescription('Self & Personnel Management');
        setUserResponses([]); // Empty responses for fallback
        setError('Using offline data - API not available');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, userId, user?.id]);

  // Access control - allow admin users to view any user's responses, client users can only view their own
  useEffect(() => {
    if (user) {
      const userIdNum = userId ? parseInt(userId, 10) : null;
      
      if (user.role === 'client_user' && user.id !== userIdNum) {
        toast.error('Access denied. You can only view your own responses.');
        navigate('/dashboard');
      } else if (user.role !== 'client_user' && user.role !== 'admin') {
        toast.error('Access denied. Insufficient permissions.');
        navigate('/dashboard');
      }
    }
  }, [user, userId, navigate]);

  // Get response for a specific question
  const getQuestionResponse = (questionId: number): string => {
    const response = userResponses.find(r => r.question_id === questionId);
    return response?.response_text || '';
  };

  // Handle response text change
  const handleResponseChange = (questionId: number, responseText: string) => {
    setUserResponses(prev => {
      const existing = prev.find(r => r.question_id === questionId);
      
      if (existing) {
        // Update existing response
        return prev.map(r => 
          r.question_id === questionId 
            ? { ...r, response_text: responseText }
            : r
        );
      } else {
        // Add new response (temporary - will be saved to server later)
        const newResponse: UserResponse = {
          id: Date.now(), // Temporary ID
          user_id: Number(userId),
          template_id: templateId!,
          question_id: questionId,
          response_text: responseText,
          interval: 'daily', // Default value
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        return [...prev, newResponse];
      }
    });
  };

  // Save responses (placeholder for future implementation)
  const handleSaveResponses = async () => {
    try {
      setSaving(true);
      // TODO: Implement API call to save responses
      toast.success('Responses saved successfully');
    } catch (err) {
      console.error('Failed to save responses:', err);
      toast.error('Failed to save responses');
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for admin view
  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Calculate completion status
  const totalQuestions = template?.questions.length || 0;
  const completedQuestions = userResponses.filter(r => r.response_text.trim() !== '').length;
  const completionPercentage = totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;

  // Show loading state
  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading worksheet...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
        {user?.role === 'admin' ? (
          <>
            {/* Admin View Layout */}
            {/* Breadcrumb */}
            <nav className="flex mb-8" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-800"
                  >
                    Dashboard
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <Link
                      to={`/organization/${orgId}`}
                      className="ml-1 text-sm font-medium text-primary-600 hover:text-primary-800 md:ml-2"
                    >
                      {organization?.name || 'Organization'}
                    </Link>
                  </div>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                      {targetUser?.name || 'User'}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>

            {/* Admin View - Read Only Card */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 mb-6" style={{background: 'linear-gradient(135deg, #667eea, #764ba2)'}}>
              <div className="flex items-center">
                <div className="text-4xl mr-4">🔒</div>
                <div className="text-white">
                  <h3 className="text-lg font-bold mb-1">ADMIN VIEW - READ ONLY</h3>
                  <p className="text-sm opacity-90">You are viewing responses for coaching/feedback purposes. Responses cannot be edited from this view.</p>
                </div>
              </div>
            </div>

            {/* User Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* User Avatar */}
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {targetUser ? getUserInitials(targetUser.name) : 'U'}
                  </div>
                  
                  {/* User Details */}
                  <div>
                    <h3 className="text-sm text-gray-500 font-medium">Viewing Responses For:</h3>
                    <h2 className="text-xl font-bold text-gray-900">{targetUser?.name || 'User Name'}</h2>
                    <p className="text-gray-600">{targetUser?.email || 'user@email.com'}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  {/* Organization Pill */}
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800 text-sm font-medium mb-3">
                    {organization?.name || 'Organization'}
                  </div>
                  
                  {/* Download PDF Button */}
                  <div>
                    <button 
                      onClick={async () => {
                        if (downloadingPdf) return; // Prevent multiple clicks
                        
                        try {
                          setDownloadingPdf(true);
                          // Make API call to generate and download PDF using the API service
                          const blob = await apiService.downloadTemplatePdf(templateId!, targetUser?.id || Number(userId));
                          
                          // Create a download link
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;

                          const currentDate = new Date().toISOString().split('T')[0]; // Y-m-d format
                          link.download = `QFM_Management_Plan_${(targetUser?.name || 'User').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${currentDate}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          
                          // Cleanup
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                          
                          toast.success('PDF downloaded successfully!');
                        } catch (error) {
                          console.error('Error downloading PDF:', error);
                          toast.error('Failed to download PDF. Please try again.');
                        } finally {
                          setDownloadingPdf(false);
                        }
                      }}
                      disabled={downloadingPdf}
                      className={`inline-flex items-center px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors ${
                        downloadingPdf 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-green-600 hover:bg-green-700'
                      } text-white`}
                    >
                      {downloadingPdf ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          <span>Generating PDF...</span>
                        </>
                      ) : (
                        <>
                          <span className="mr-2">📥</span>
                          <span>Download PDF</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Client User Header Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8" style={{ height: '65px' }}>
              <div className="flex items-center justify-between h-full px-6">
                {/* Left Side - Breadcrumb with non-editable template name */}
                <div className="flex items-center space-x-2">
                  {/* Back arrow and navigation */}
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm flex items-center space-x-1 transition-all duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    <span>Career Development Worksheets</span>
                  </button>
                  
                  <span className="text-gray-400 text-sm">/</span>
                  
                  {/* Template Name */}
                  <h1 className="text-lg font-bold text-gray-900">
                    {templateName}
                  </h1>
                </div>

                {/* Right Side - Auto-saved indicator and mode toggle */}
                <div className="flex items-center space-x-4">
                  {/* Auto-saved indicator */}
                  <div className={`flex items-center space-x-1 text-sm transition-colors duration-200 ${
                    autoSavedVisible ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Auto-saved</span>
                  </div>

                  {/* Mode Toggle Buttons */}
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      className={`px-4 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                        currentMode === 'prep' 
                          ? 'bg-secondary-500 text-white shadow-sm' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      onClick={() => handleModeChange('prep')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      <span>Prep Mode</span>
                    </button>
                    <button
                      className={`px-4 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                        currentMode === 'present' 
                          ? 'bg-secondary-500 text-white shadow-sm' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      onClick={() => handleModeChange('present')}
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4" >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18v12H3V6zm9 0v12M3 12h18" />
                      </svg>
                      <span>Table Mode</span>
                    </button>
                    <button
                      className={`px-4 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                        currentMode === 'plan' 
                          ? 'bg-secondary-500 text-white shadow-sm' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      onClick={() => handleModeChange('plan')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>My Plan</span>
                    </button>
                  </div>
                  
                  {/* Overview Link */}
                  <button
                    onClick={() => navigate(`/organization/${orgId}/user/${userId}/welcome`)}
                    className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm flex items-center space-x-1 transition-all duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Overview</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main Content Area with Fade Transition */}
        <div className={`flex-1 flex transition-opacity duration-200 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          {user?.role === 'admin' ? (
            <>
              {/* Admin View - Only show My Plan mode in full width */}
              <div className="flex-1 flex justify-center">
                <div className="w-full">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    {/* Header Section */}
                    <div className="text-center mb-8">
                      <h1 className="text-4xl font-bold text-gray-700 mb-4">Management Plan</h1>
                      <p className="text-gray-400 text-sm mb-1">
                        {targetUser?.name || 'User Name'} | {organization?.name || 'No Organization'}
                      </p>
                      <p className="text-gray-400 mb-6 text-sm">
                        Generated: {new Date().toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      <div className="border-b-4 border-secondary-600"></div>
                    </div>
                    
                    {/* Steps and Questions */}
                    <div className="space-y-8">
                      {steps.map((step) => {
                        const stepQuestions = questions.filter(q => q.step_number === step.step_number);
                        const stepQuestionsWithResponses = stepQuestions.filter(q => {
                          const questionResponses = userResponses.filter(r => r.question_id === q.id && r.response_text.trim());
                          return questionResponses.length > 0;
                        });
                        
                        // Only show steps that have questions with responses
                        if (stepQuestionsWithResponses.length === 0) {
                          return null;
                        }
                        
                        return (
                          <div key={step.id} className="mb-8">
                            {/* Step Header */}
                            <h2 className="text-2xl font-semibold text-secondary-600 mb-4 pb-4 border-b border-gray-300">
                              Step {step.step_number}: {step.step_name}
                            </h2>
                            
                            {/* Questions and Responses */}
                            <div className="space-y-6">
                              {stepQuestionsWithResponses.map((question) => {
                                const questionResponses = userResponses.filter(r => 
                                  r.question_id === question.id && r.response_text.trim()
                                );
                                
                                // Group responses by interval
                                const responsesByInterval = {
                                  daily: questionResponses.filter(r => r.interval === 'daily'),
                                  weekly: questionResponses.filter(r => r.interval === 'weekly'),
                                  monthly: questionResponses.filter(r => r.interval === 'monthly'),
                                  quarterly: questionResponses.filter(r => r.interval === 'quarterly'),
                                  yearly: questionResponses.filter(r => r.interval === 'yearly')
                                };
                                
                                return (
                                  <div key={question.id} className="mb-6">
                                    {/* Question Text */}
                                    <h3 className="text-md font-bold text-gray-600 mb-3">
                                      {question.question_text}
                                    </h3>
                                    
                                    {/* Responses by Interval */}
                                    <div className="space-y-3">
                                      {(['daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const).map((interval) => {
                                        const responses = responsesByInterval[interval];
                                        if (responses.length === 0) return null;
                                        
                                        return (
                                          <div key={interval} className="ml-4 text-sm">
                                            <h4 className="text-base font-medium text-gray-500 mb-2 capitalize text-sm">
                                              {interval}:
                                            </h4>
                                            <div className="space-y-2">
                                              {responses.map((response) => (
                                                <div key={response.id} className="flex items-start space-x-2">
                                                  <div className="w-1 h-1 rounded-full bg-secondary-500 mt-2 flex-shrink-0"></div>
                                                  <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                                                    {response.response_text}
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Show message if no responses exist */}
                      {steps.every(step => {
                        const stepQuestions = questions.filter(q => q.step_number === step.step_number);
                        const stepQuestionsWithResponses = stepQuestions.filter(q => {
                          const questionResponses = userResponses.filter(r => r.question_id === q.id && r.response_text.trim());
                          return questionResponses.length > 0;
                        });
                        return stepQuestionsWithResponses.length === 0;
                      }) && (
                        <div className="text-center py-12">
                          <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-600 mb-2">No Responses Available</h3>
                          <p className="text-gray-500">
                            This user has not completed any responses for the assigned worksheet.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Admin Note */}
                    <div className="mt-8 text-center pt-8 border-t-4 border-secondary-600">
                      <p className="text-sm text-gray-500 italic">
                        ℹ️ Note: This is a read-only view. To modify responses, the user must edit them from their own account.
                      </p>
                    </div>

                    {/* Bottom Download PDF Button */}
                    <div className="mt-6 flex justify-center">
                      <button 
                        onClick={async () => {
                          if (downloadingPdf) return; // Prevent multiple clicks
                          
                          try {
                            setDownloadingPdf(true);
                            // Make API call to generate and download PDF using the API service
                            const blob = await apiService.downloadTemplatePdf(templateId!, targetUser?.id || Number(userId));
                            
                            // Create a download link
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;

                            const currentDate = new Date().toISOString().split('T')[0]; // Y-m-d format
                            link.download = `QFM_Management_Plan_${(targetUser?.name || 'User').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${currentDate}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            
                            // Cleanup
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            
                            toast.success('PDF downloaded successfully!');
                          } catch (error) {
                            console.error('Error downloading PDF:', error);
                            toast.error('Failed to download PDF. Please try again.');
                          } finally {
                            setDownloadingPdf(false);
                          }
                        }}
                        disabled={downloadingPdf}
                        className={`inline-flex items-center px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors ${
                          downloadingPdf 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-green-600 hover:bg-green-700'
                        } text-white`}
                      >
                        {downloadingPdf ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            <span>Generating PDF...</span>
                          </>
                        ) : (
                          <>
                            <span className="mr-2">📥</span>
                            <span>Download PDF</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : currentMode === 'prep' ? (
            <>
              {/* Response Mode: Sidebar + KanbanBoard Layout */}
              <StepSidebar
                steps={steps}
                questions={questions}
                activeStepId={activeStepId}
                onStepSelect={handleStepSelect}
                isClientView={true}
                userResponses={userResponses}
              />

              {/* Main Content */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 flex flex-col">
                  <KanbanBoard
                    steps={steps}
                    questions={questions}
                    activeStepId={activeStepId}
                    onQuestionsUpdate={handleQuestionsUpdate}
                    onEditQuestion={handleEditQuestionFromKanban}
                    onDeleteQuestion={handleDeleteQuestionFromKanban}
                    onAddQuestion={handleAddQuestionFromKanban}
                    onInsertQuestion={handleInsertQuestion}
                    onReorderQuestion={handleReorderQuestion}
                    deletingQuestionId={null}
                    isOperationLoading={false}
                    isPresentMode={false}
                    isReadOnly={true}
                    isClientView={currentMode === 'prep'}
                    userResponses={userResponses}
                    onAddResponse={async (questionId, responseText, interval) => {
                      try {
                        const newResponse = await apiService.createResponse({
                          user_id: Number(userId),
                          template_id: templateId!,
                          question_id: questionId,
                          response_text: responseText,
                          interval: interval as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
                        });
                        setUserResponses(prev => [...prev, newResponse]);
                        toast.success('Response added successfully');
                      } catch (err) {
                        console.error('Failed to create response:', err);
                        toast.error('Failed to add response');
                      }
                    }}
                    onEditResponse={async (responseId, responseText, interval) => {
                      try {
                        const updatedResponse = await apiService.updateResponse(responseId, {
                          response_text: responseText,
                          interval: interval as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
                        });
                        setUserResponses(prev => 
                          prev.map(r => 
                            r.id === responseId ? updatedResponse : r
                          )
                        );
                        toast.success('Response updated successfully');
                      } catch (err) {
                        console.error('Failed to update response:', err);
                        toast.error('Failed to update response');
                      }
                    }}
                    onDeleteResponse={async (responseId) => {
                      try {
                        await apiService.deleteResponse(responseId);
                        setUserResponses(prev => prev.filter(r => r.id !== responseId));
                        toast.success('Response deleted successfully');
                      } catch (err) {
                        console.error('Failed to delete response:', err);
                        toast.error('Failed to delete response');
                      }
                    }}
                  />
                </div>
              </div>
            </>
          ) : currentMode === 'present' ? (
            <>
              {/* Present Mode: Full Width KanbanBoard */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 flex flex-col">
                  <KanbanBoard
                    steps={steps}
                    questions={questions}
                    activeStepId={null} // Show all steps in present mode
                    onQuestionsUpdate={handleQuestionsUpdate}
                    onEditQuestion={handleEditQuestionFromKanban}
                    onDeleteQuestion={handleDeleteQuestionFromKanban}
                    onAddQuestion={handleAddQuestionFromKanban}
                    onInsertQuestion={handleInsertQuestion}
                    onReorderQuestion={handleReorderQuestion}
                    deletingQuestionId={null}
                    isOperationLoading={false}
                    isPresentMode={true}
                    isReadOnly={true}
                    isClientView={true}
                    userResponses={userResponses}
                    onQuestionClick={handleQuestionClick}
                    onAddResponse={async (questionId, responseText, interval) => {
                      try {
                        const newResponse = await apiService.createResponse({
                          user_id: Number(userId),
                          template_id: templateId!,
                          question_id: questionId,
                          response_text: responseText,
                          interval: interval as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
                        });
                        setUserResponses(prev => [...prev, newResponse]);
                        toast.success('Response added successfully');
                      } catch (err) {
                        console.error('Failed to create response:', err);
                        toast.error('Failed to add response');
                      }
                    }}
                    onEditResponse={async (responseId, responseText, interval) => {
                      try {
                        const updatedResponse = await apiService.updateResponse(responseId, {
                          response_text: responseText,
                          interval: interval as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
                        });
                        setUserResponses(prev => 
                          prev.map(r => 
                            r.id === responseId ? updatedResponse : r
                          )
                        );
                        toast.success('Response updated successfully');
                      } catch (err) {
                        console.error('Failed to update response:', err);
                        toast.error('Failed to update response');
                      }
                    }}
                    onDeleteResponse={async (responseId) => {
                      try {
                        await apiService.deleteResponse(responseId);
                        setUserResponses(prev => prev.filter(r => r.id !== responseId));
                        toast.success('Response deleted successfully');
                      } catch (err) {
                        console.error('Failed to delete response:', err);
                        toast.error('Failed to delete response');
                      }
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* My Plan Mode: Custom layout for plan view */}
              <div className="flex-1 flex justify-center">
                <div className="w-full max-w-4xl">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    {/* Header Section */}
                    <div className="text-center mb-8">
                      <h1 className="text-4xl font-bold text-gray-700 mb-4">My Management Plan</h1>
                      <p className="text-gray-400 text-sm mb-1">
                        {user?.name || 'User Name'} | {user?.organization?.name || 'No Organization'}
                      </p>
                      <p className="text-gray-400 mb-6 text-sm">
                        Generated: {new Date().toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      <div className="border-b-4 border-secondary-600"></div>
                    </div>
                    
                    {/* Steps and Questions */}
                    <div className="space-y-8">
                      {steps.map((step) => {
                        const stepQuestions = questions.filter(q => q.step_number === step.step_number);
                        const stepQuestionsWithResponses = stepQuestions.filter(q => {
                          const questionResponses = userResponses.filter(r => r.question_id === q.id && r.response_text.trim());
                          return questionResponses.length > 0;
                        });
                        
                        // Only show steps that have questions with responses
                        if (stepQuestionsWithResponses.length === 0) {
                          return null;
                        }
                        
                        return (
                          <div key={step.id} className="mb-8">
                            {/* Step Header */}
                            <h2 className="text-2xl font-semibold text-secondary-600 mb-4 pb-4 border-b border-gray-300">
                              Step {step.step_number}: {step.step_name}
                            </h2>
                            
                            {/* Questions and Responses */}
                            <div className="space-y-6">
                              {stepQuestionsWithResponses.map((question) => {
                                const questionResponses = userResponses.filter(r => 
                                  r.question_id === question.id && r.response_text.trim()
                                );
                                
                                // Group responses by interval
                                const responsesByInterval = {
                                  daily: questionResponses.filter(r => r.interval === 'daily'),
                                  weekly: questionResponses.filter(r => r.interval === 'weekly'),
                                  monthly: questionResponses.filter(r => r.interval === 'monthly'),
                                  quarterly: questionResponses.filter(r => r.interval === 'quarterly'),
                                  yearly: questionResponses.filter(r => r.interval === 'yearly')
                                };
                                
                                return (
                                  <div key={question.id} className="mb-6">
                                    {/* Question Text */}
                                    <h3 className="text-md font-bold text-gray-600 mb-3">
                                      {question.question_text}
                                    </h3>
                                    
                                    {/* Responses by Interval */}
                                    <div className="space-y-3">
                                      {(['daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const).map((interval) => {
                                        const responses = responsesByInterval[interval];
                                        if (responses.length === 0) return null;
                                        
                                        return (
                                          <div key={interval} className="ml-4 text-sm">
                                            <h4 className="text-base font-medium text-gray-500 mb-2 capitalize text-sm">
                                              {interval}:
                                            </h4>
                                            <div className="space-y-2">
                                              {responses.map((response) => (
                                                <div key={response.id} className="flex items-start space-x-2">
                                                  {/* <div className="w-1 h-1 rounded-full bg-secondary-500 mt-2 flex-shrink-0"></div> */}
                                                  <p className="text-gray-800 leading-relaxed whitespace-pre-line ml-4">
                                                    {response.response_text}
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Show message if no responses exist */}
                      {steps.every(step => {
                        const stepQuestions = questions.filter(q => q.step_number === step.step_number);
                        const stepQuestionsWithResponses = stepQuestions.filter(q => {
                          const questionResponses = userResponses.filter(r => r.question_id === q.id && r.response_text.trim());
                          return questionResponses.length > 0;
                        });
                        return stepQuestionsWithResponses.length === 0;
                      }) && (
                        <div className="text-center py-12">
                          <div className="text-gray-400 mb-4">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-600 mb-2">No Plan Available</h3>
                          <p className="text-gray-500">
                            Complete your responses in Response Mode to generate your personalized management plan.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Export Actions */}
                    <div className="export-actions flex justify-center space-x-4 mt-8 pt-6 border-t border-gray-200">
                      <button
                        onClick={async () => {
                          if (downloadingPdf) return; // Prevent multiple clicks
                          
                          try {
                            setDownloadingPdf(true);
                            // Make API call to generate and download PDF using the API service
                            const blob = await apiService.downloadTemplatePdf(templateId!, user?.id || Number(userId));
                            
                            // Create a download link
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;

                            const currentDate = new Date().toISOString().split('T')[0]; // Y-m-d format
                            link.download = `QFM_Management_Plan_${(user?.name || 'User').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${currentDate}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            
                            // Cleanup
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            
                            toast.success('PDF downloaded successfully!');
                          } catch (error) {
                            console.error('Error downloading PDF:', error);
                            toast.error('Failed to download PDF. Please try again.');
                          } finally {
                            setDownloadingPdf(false);
                          }
                        }}
                        disabled={downloadingPdf}
                        className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors duration-200 ${
                          downloadingPdf 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-primary-500 hover:bg-primary-700'
                        } text-white`}
                      >
                        {downloadingPdf ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Generating PDF...</span>
                          </>
                        ) : (
                          <>
                            <span>📥</span>
                            <span>Download PDF</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => {
                          // Add print-specific class and trigger print
                          document.body.classList.add('printing');
                          setTimeout(() => {
                            window.print();
                            setTimeout(() => {
                              document.body.classList.remove('printing');
                            }, 100);
                          }, 100);
                        }}
                        className="flex items-center space-x-2 px-6 py-3 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors duration-200"
                      >
                        <span>🖨️</span>
                        <span>Print</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Question Detail Modal - Only show in Present Mode */}
      {currentMode === 'present' && selectedQuestionId && (
        <QuestionDetailSidebar
          question={questions.find(q => q.id === selectedQuestionId) || null}
          step={(() => {
            const question = questions.find(q => q.id === selectedQuestionId);
            return question ? steps.find(s => s.step_number === question.step_number) || null : null;
          })()}
          userResponses={userResponses.filter(r => r.question_id === selectedQuestionId)}
          questionOrder={selectedQuestionOrder || 0}
          onClose={() => setSelectedQuestionId(null)}
          onAddResponse={async (questionId, responseText, interval) => {
            try {
              const newResponse = await apiService.createResponse({
                user_id: Number(userId),
                template_id: templateId!,
                question_id: questionId,
                response_text: responseText,
                interval: interval as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
              });
              setUserResponses(prev => [...prev, newResponse]);
              toast.success('Response added successfully');
            } catch (err) {
              console.error('Failed to create response:', err);
              toast.error('Failed to add response');
            }
          }}
          onEditResponse={async (responseId, responseText, interval) => {
            try {
              const updatedResponse = await apiService.updateResponse(responseId, {
                response_text: responseText,
                interval: interval as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
              });
              setUserResponses(prev => 
                prev.map(r => 
                  r.id === responseId ? updatedResponse : r
                )
              );
              toast.success('Response updated successfully');
            } catch (err) {
              console.error('Failed to update response:', err);
              toast.error('Failed to update response');
            }
          }}
          onDeleteResponse={async (responseId) => {
            try {
              await apiService.deleteResponse(responseId);
              setUserResponses(prev => prev.filter(r => r.id !== responseId));
              toast.success('Response deleted successfully');
            } catch (err) {
              console.error('Failed to delete response:', err);
              toast.error('Failed to delete response');
            }
          }}
        />
      )}
    </Layout>
  );
};

export default RespondTemplate;